import { useQuery } from '@apollo/client'
import { Separator } from '@farol-ds/react'
import { withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import {
  buildExperimentContext,
  buildMetricsProperties,
  DocPopoverElement,
  DocSearch,
  DocSearchElement,
  Docview,
  getDocumentStatusCode,
  isGoogleBotAgent,
  JUSBRASIL_HOTJAR_SITE_ID,
  MetricsProperties,
  printStyles,
  RecommendedDocuments,
  redirectDocumentUrl,
  SeusDireitosWidgetLazy,
  shouldConsumeViewCredits,
  useConsumeCredits,
  useDwellTime,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import { SetNonNullableQueryRootProps } from '@jusbrasil-web/shared-apollo-client/utilities'
import { AppBar } from '@jusbrasil-web/shared-appbar'
import { COMPONENT_CLASS, FEEDBACK_NAME } from '@jusbrasil-web/shared-ga4-events'
import { hotjarEvent, HotjarScript } from '@jusbrasil-web/shared-hotjar-script'
import { ServerSidePersistentAlertBanner } from '@jusbrasil-web/shared-persistent-alert-banner'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useEffect, useMemo, useRef } from 'react'
import UAParser from 'ua-parser-js'

import {
  ArticleNewsDocument,
  ArticleNewsMetaTags,
  ArticleNewsToolbar,
  Comments,
  ModerationBanner,
  PublicationAppbar,
  PublicationHeader,
  PublicationSidebar,
} from '../../components'
import { getActiveExperiments } from '../../experiment'
import { useDisableGoogleOneTap } from '../../hooks/useDisableGoogleOneTap'
import { extractFromUrl } from '../../utils'
import { getUserAgentProperties } from '../../utils/user-agent'
import styles from '../publications.module.scss'
import { NewsRootQuery, NewsRootQueryVariables } from './__generated__/query.graphql'
import { NewsRoot } from './query'

/* eslint-disable-next-line @typescript-eslint/no-empty-interface */
export interface NewsPageProps {
  variables: NewsRootQueryVariables
  metricsProperties: MetricsProperties
  disableTouchFeatures?: boolean
  httpError?: true
  shouldConsumeViewCredits?: boolean
  isGoogleBotAgent?: boolean
}

type NewsRootQueryPage = SetNonNullableQueryRootProps<NewsRootQuery, 'document'>

const eventData = {
  app_id: 'web-docview',
  from_component: 'docview/NewsPage',
}

export function NewsPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const {
    disableTouchFeatures,
    httpError,
    isGoogleBotAgent,
    metricsProperties,
    shouldConsumeViewCredits,
    variables,
  } = props

  const { data } = useQuery<NewsRootQueryPage, NewsRootQueryVariables>(NewsRoot, { variables })
  const { document, viewer = null, persistentAlertBannerViewer = null } = data?.root || {}

  const { ref: dispatcherRef, sendMetrics, sendCopyEvents } = useMetrics<HTMLDivElement>()
  const contentRef = useRef<HTMLDivElement>(null)
  const docSearchRef = useRef<DocSearchElement>(null)
  const docPopoverRef = useRef<DocPopoverElement>(null)

  const hasPaidPlan = !!viewer?.hasPaidPlan
  const userAgentProps = getUserAgentProperties()

  useConsumeCredits(shouldConsumeViewCredits, document)
  useDwellTime(document, sendMetrics, metricsProperties)

  useDisableGoogleOneTap(!!viewer)

  const newsHeaderToolbar = useMemo(
    () =>
      document ? (
        <ArticleNewsToolbar
          document={document}
          viewer={viewer}
          docSearchRef={docSearchRef}
          ga4EventData={{
            componentClass: COMPONENT_CLASS.DOCUMENT_HEADER,
            resourceId: document.boxResourceId,
            feedbackName: FEEDBACK_NAME.NEWS_CONTENT_FEEDBACK,
          }}
        />
      ) : null,
    [document, viewer]
  )

  const newsAppbarToolbar = useMemo(
    () =>
      document ? (
        <ArticleNewsToolbar
          document={document}
          viewer={viewer}
          docSearchRef={docSearchRef}
          ga4EventData={{
            componentClass: COMPONENT_CLASS.DOCUMENT_TOPBAR,
            resourceId: document.boxResourceId,
            feedbackName: FEEDBACK_NAME.NEWS_CONTENT_FEEDBACK,
          }}
        />
      ) : null,
    [document, viewer]
  )

  useEffect(() => {
    sendMetrics('Doc.Viewed', metricsProperties)
    hotjarEvent('event', 'ExperimentNewsFarolRollout')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.docId])

  useEffect(() => {
    if (viewer?.isLawyer || viewer?.hasPaidPlan || viewer?.isUserInMrBigCNJIntegration) return

    const copy = 'Fale agora com um advogado online'

    sendMetrics('SeusDireitos.CtaView', {
      app_id: 'web-docview',
      page: 'docview-noticia',
      browser: userAgentProps.browser,
      os: userAgentProps.os,
      user_agent: userAgentProps.user_agent,
      dvce_type: userAgentProps.dvce_type,
      dvce_ismobile: userAgentProps.dvce_ismobile,
      device_name: userAgentProps.device_name,
      metadata: { copy, page: 'docview-noticia', doc_id: metricsProperties.doc_id },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer, metricsProperties, userAgentProps])

  if (httpError || !document) return null

  const handleCopy = () => {
    sendCopyEvents(metricsProperties)
  }

  const onLoadMoreComments = () => docPopoverRef.current?.refreshElementNodes()

  return (
    <AppBar.Provider>
      <ServerSidePersistentAlertBanner persistentAlertBannerViewer={persistentAlertBannerViewer} />
      <Docview
        document={document}
        viewer={viewer}
        handleCopy={handleCopy}
        metricsProperties={metricsProperties}
        canCopyContent={true}
        dispatcherRef={dispatcherRef}
        contentRef={dispatcherRef}
        docPopoverRef={docPopoverRef}
        disableTouchFeatures={!!disableTouchFeatures}
        topbarCurrentNavKey="NOTICIAS"
        metaTags={<ArticleNewsMetaTags document={document} />}
        afterTopbar={
          <>
            {hasPaidPlan && (
              <DocSearch
                contentRef={contentRef}
                ref={docSearchRef}
                slug={document.slug || undefined}
              />
            )}
          </>
        }
        header={
          <>
            <ModerationBanner document={document} viewer={viewer} />
            <PublicationHeader document={document} viewer={viewer} toolbar={newsHeaderToolbar} />
            <AppBar.StickyTrigger />
          </>
        }
        asideContent={<PublicationSidebar document={document} />}
        mainContent={<ArticleNewsDocument document={document} contentRef={contentRef} />}
        relatedContent={
          <>
            <RecommendedDocuments document={document} />
            {document.recommendations.length > 0 && (
              <Separator
                className={classNames(
                  printStyles.unprintable,
                  styles.recommendedDocumentsSeparator
                )}
              />
            )}
            <Comments
              document={document}
              viewer={viewer}
              onLoadMore={onLoadMoreComments}
              className={printStyles.unprintable}
            />
          </>
        }
        endContent={
          <PublicationAppbar
            document={document}
            className={printStyles.unprintable}
            toolbar={newsAppbarToolbar}
          />
        }
      />

      <HotjarScript hjid={JUSBRASIL_HOTJAR_SITE_ID} />

      <SeusDireitosWidgetLazy
        viewer={viewer}
        className={styles.sofiaWrapper}
        page="docview-noticia"
        sendMetrics={sendMetrics}
      />
    </AppBar.Provider>
  )
}

const getServerSideProps: GetServerSideProps<NewsPageProps> = async (ctx) => {
  const { parts } = ctx.query
  if (!parts) {
    return {
      notFound: true,
    }
  }
  const { docId } = extractFromUrl(Array.isArray(parts) ? parts : [parts])
  if (!docId) {
    return {
      notFound: true,
    }
  }

  const variables: NewsRootQueryVariables = {
    docId,
    isNewsPage: true,
  }
  const { client, withApolloProps } = withApollo(ctx)
  const { data } = await client.query<NewsRootQuery>({
    query: NewsRoot,
    variables,
  })
  const { document, viewer } = data?.root ?? {}
  if (!document) {
    return {
      notFound: true,
    }
  }

  const experimentContext = await buildExperimentContext(ctx, getActiveExperiments())
  const metricsProperties = buildMetricsProperties(ctx, document, viewer, experimentContext)

  const status = getDocumentStatusCode(ctx.req, document, viewer)
  if (status !== 200) {
    ctx.res.statusCode = status
    return {
      props: {
        variables,
        metricsProperties,
        httpError: true,
      },
    }
  }

  const redirect = document.url && redirectDocumentUrl(ctx, document.url)
  if (redirect) {
    return { redirect }
  }

  const ua = UAParser(ctx.req.headers['user-agent'])
  const disableTouchFeatures = ua.device.type === 'mobile' || ua.device.type === 'tablet'

  return {
    props: {
      ...withApolloProps(),
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      variables,
      metricsProperties,
      disableTouchFeatures,
      shouldConsumeViewCredits: shouldConsumeViewCredits(ctx.req, viewer),
      isGooglebotAgent: isGoogleBotAgent(ctx.req),
    },
  }
}

export const NewsPageExports = { getServerSideProps }
